import { useEffect, useState } from "react";
import { Box, Button, InputAdornment, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SideDrawer, {
  FieldLabel,
  dateInputToISO,
  fieldSx,
  primaryButtonSx,
  todayISODate,
} from "components/SideDrawer";
import { useToast } from "components/Toast";
import { DateField } from "components/FormPickers";
import { addRevenue, getApiError } from "services/finance";

const AddRevenueDrawer = ({
  open,
  onClose,
  bookingRef,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  bookingRef?: string;
  onSuccess?: () => void;
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    description: "",
    amount: "",
    date: todayISODate(),
    notes: "",
    bookingRef: "",
  });

  useEffect(() => {
    if (open) {
      setForm({
        description: "",
        amount: "",
        date: todayISODate(),
        notes: "",
        bookingRef: bookingRef || "",
      });
    }
  }, [open, bookingRef]);

  const mutation = useMutation({
    mutationFn: () =>
      addRevenue({
        description: form.description.trim(),
        amount: Number(form.amount),
        date: dateInputToISO(form.date),
        notes: form.notes.trim(),
        bookingRef: form.bookingRef.trim(),
      }),
    onSuccess: (res) => {
      showToast(res?.message || "Revenue added", "success");
      queryClient.invalidateQueries({ queryKey: ["finance"] });
      queryClient.invalidateQueries({ queryKey: ["flightFinance"] });
      onSuccess?.();
      onClose();
    },
    onError: (error: any) =>
      showToast(getApiError(error, "Failed to add revenue"), "error"),
  });

  const set = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const canSubmit =
    form.description.trim() && Number(form.amount) > 0 && form.date;

  return (
    <SideDrawer open={open} onClose={onClose} title="Add Revenue">
      <Box display="flex" flexDirection="column" gap="16px">
        <Box>
          <FieldLabel>Description</FieldLabel>
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            placeholder="Visa assistance fee"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Box>
        <Box>
          <FieldLabel>Amount</FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="number"
            sx={fieldSx}
            placeholder="25,000"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">₦</InputAdornment>,
            }}
          />
        </Box>
        <Box>
          <FieldLabel>Date</FieldLabel>
          <DateField
            value={form.date}
            onChange={(value) => set("date", value)}
            disableFuture
          />
        </Box>
        <Box>
          <FieldLabel>Booking Ref (optional)</FieldLabel>
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            placeholder="PNR, booking ID or ride code"
            value={form.bookingRef}
            disabled={Boolean(bookingRef)}
            onChange={(e) => set("bookingRef", e.target.value)}
          />
        </Box>
        <Box>
          <FieldLabel>Notes</FieldLabel>
          <TextField
            fullWidth
            multiline
            rows={3}
            sx={fieldSx}
            placeholder="Paid by transfer"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Box>

        <Button
          fullWidth
          variant="contained"
          sx={{ ...primaryButtonSx, mt: "8px" }}
          disabled={!canSubmit || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Adding..." : "Add Revenue"}
        </Button>
        <Button
          onClick={onClose}
          sx={{ color: "#101828", textDecoration: "underline", textTransform: "none" }}
        >
          Cancel
        </Button>
      </Box>
    </SideDrawer>
  );
};

export default AddRevenueDrawer;
